/**
 * Get a descriptive message for recently activated accounts
 * @param count Number of recently activated accounts (or undefined)
 * @returns A message describing the recently activated accounts
 */
export function getRecentlyActivatedAccountsMessage(count: number): string {
  if (count === 0) {
    return "No accounts have been activated in the past month.";
  } else if (count === 1) {
    return "1 account has been activated in the past month.";
  } else {
    return `${count.toLocaleString()} accounts have been activated in the past month.`;
  }
}

/**
 * Format power value with appropriate unit (kWh, MWh, GWh, TWh)
 * Converts to next unit when approaching 1,000 of the previous unit
 * @param mwh Total power delivered in megawatt-hours
 * @returns Object with formatted number and unit descriptor
 */
export function formatPowerDelivered(mwh: number): {
  value: string;
  unit: string;
} {
  // Convert MWh to kWh for base calculations
  const kwh = mwh * 1000;

  if (kwh === 0) {
    return { value: "0", unit: "kilowatt-hours" };
  } else if (kwh < 1000) {
    // Use kilowatt-hours
    return { value: kwh.toFixed(1), unit: "kilowatt-hours" };
  } else if (mwh < 1000) {
    // Use megawatt-hours
    return { value: mwh.toFixed(1), unit: "megawatt-hours" };
  } else if (mwh < 1000000) {
    // Use gigawatt-hours
    const gwh = mwh / 1000;
    return { value: gwh.toFixed(1), unit: "gigawatt-hours" };
  } else {
    // Use terawatt-hours
    const twh = mwh / 1000000;
    if (twh > 1000) {
      // if we are greater than 1,000 TWh,
      // then we'll show a `k` suffix.
      // e.g 1000 TWh -> "1K TWh"
      const pwh = twh / 1000;
      return { value: `${pwh.toFixed(0)}k`, unit: "terawatt-hours" };
    }
    return { value: twh.toFixed(1), unit: "terawatt-hours" };
  }
}

/**
 * Get a descriptive message based on total power delivered in megawatt-hours (MWh)
 * @param mwh Total power delivered in megawatt-hours
 * @returns A contextual message describing the power delivery milestone
 */
export function getPowerDeliveredMessage(mwh: number): string {
  if (mwh <= 0) {
    return "You'll start seeing this metric grow a few weeks after your first meter read.";
  }
  // Convert MWh to kWh for calculations
  const kwh = mwh * 1000;
  const messageWithMinKhw: [number, number, (amt: string) => string][] = [
    [
      0,
      0.015,
      (amt: string) =>
        `That's enough renewable energy to boil ${amt} cups of tea.`,
    ],
    [
      1.0,
      0.01,
      (amt: string) =>
        `That's enough renewable energy to power a light bulb for ${amt} hours.`,
    ],
    [
      100.0,
      50,
      (amt: string) =>
        `That's enough renewable energy to use ${amt} electric bikes for a month.`,
    ],
    [
      1000,
      3.6,
      (amt: string) =>
        `That's enough renewable energy to drive an EV ${amt} miles.`,
    ],
    [
      2000,
      670,
      (amt: string) =>
        `That's enough renewable energy to drive an EV from NYC to LA ${amt} times.`,
    ],
    [
      3000,
      30,
      (amt: string) =>
        `That's ${amt} days worth of renewable energy use for an average home.`,
    ],
    [
      30000,
      15000,
      (amt: string) =>
        `That's enough renewable energy to power ${amt} NFL stadiums on game day.`,
    ],
    [
      1236000,
      1236000,
      (amt: string) =>
        `That's enough renewable energy to power all of Netflix streaming for ${amt} ${amt === "1" ? "day" : "days"}.`,
    ],
  ];
  const potentialMessages = messageWithMinKhw.filter(
    ([minKwh]) => kwh >= minKwh,
  );
  if (potentialMessages.length === 0) {
    return "You'll start seeing this metric grow a few weeks after your first meter read.";
  }
  const randomlyChosenMessage =
    potentialMessages[Math.floor(Math.random() * potentialMessages.length)];
  const [, divisor, formatFunc] = randomlyChosenMessage;
  const calculatedValue = Math.floor(kwh / divisor).toLocaleString();
  return formatFunc(calculatedValue);
}
